import React, {useRef, useCallback, useState, useEffect} from 'react';
import {Box, styled} from '@mui/material';
import {designSystemToken, Icon, Label} from '@lightricks/react-design-system';
import VideoCaptionOverlayStandalone from '@contentlab/components/video-display/captions-rendering/VideoCaptionOverlayStandalone';
import {contentLabActions} from '@contentlab/stores/contentLabStore';
import {CaptionsStylePreset, Caption} from '@contentlab/types/captions';
import translate from '@/utils/translate';
import {scrollbarMixins} from '@/styles/mixins';

const Container = styled(Box)(() => ({
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  borderRadius: '32px',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  margin: 0,
  padding: 0,
}));

const ContentWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'center',
  justifyContent: 'start',
  padding: '0px 24px 24px 24px',
  flex: 1,
  overflow: 'auto',
  minHeight: 0,
  boxSizing: 'border-box',
  margin: 0,
  ...scrollbarMixins.overrideScrollbar(),
}));

const StylesGrid = styled(Box)(() => ({
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0px, 1fr))',
  gap: '16px',
  width: '100%',
  alignContent: 'start',
  boxSizing: 'border-box',
}));

const StyleCard = styled(Box, {
  shouldForwardProp: (prop: string) => !['isSelected'].includes(prop),
})<{isSelected?: boolean}>(({isSelected}) => ({
  'backgroundColor': designSystemToken('semantic.bg.disabled-secondary'),
  'border': isSelected
    ? `1px solid ${designSystemToken('semantic.bg.inverse')}`
    : '1px solid transparent',
  'display': 'flex',
  'flexDirection': 'column',
  'gap': '6px',
  'alignItems': 'center',
  'justifyContent': 'center',
  'padding': '16px',
  'borderRadius': '16px',
  'cursor': 'pointer',
  'transition': 'all 0.2s ease-in-out',
  'position': 'relative',
  'height': '164px',
  'boxSizing': 'border-box',
  '&:hover .hover-buttons': {
    opacity: 1,
  },
  '&:hover .edited-indicator': {
    opacity: 0,
  },
}));

const PreviewContainer = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: designSystemToken('semantic.bg.disabled-secondary'),
  borderRadius: '16px',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const indictorToHoverButtonsTransition = 'opacity 0.2s ease-in-out';

const shouldNotForwardProps = ['isActive'];

const HoverButtons = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({isActive}: {isActive: boolean}) => ({
  position: 'absolute',
  boxSizing: 'border-box',
  justifyContent: 'space-between',
  paddingInline: '7px',
  width: '100%',
  top: '8px',
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
  opacity: isActive ? 1 : 0,
  transition: indictorToHoverButtonsTransition,
}));

const IconButton = styled(Box)(() => ({
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'width': '32px',
  'height': '32px',
  'backgroundColor': designSystemToken('semantic.bg.secondary'),
  'borderRadius': '50%',
  'cursor': 'pointer',
  '&:hover': {
    backgroundColor: designSystemToken('semantic.bg.tertiary'),
  },
  'zIndex': 10,
}));

const LabelIconButton = styled(Box)(() => ({
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'padding': '0 12px 0 6px',
  'gap': '4px',
  'height': '32px',
  'backgroundColor': designSystemToken('semantic.bg.secondary'),
  'borderRadius': '999px',
  'cursor': 'pointer',
  '&:hover': {
    backgroundColor: designSystemToken('semantic.bg.tertiary'),
  },
  'zIndex': 10,
}));

interface HoverButtonProps {
  iconName: 'Actions-Reset' | 'Features-Makeup-Liner';
  onClick: (e: React.MouseEvent) => void;
  ariaLabel: string;
  label?: string;
}

function HoverButton({iconName, onClick, ariaLabel, label}: HoverButtonProps) {
  const WrapperElement = label ? LabelIconButton : IconButton;
  return (
    <WrapperElement onClick={onClick} aria-label={ariaLabel}>
      <Icon
        name={iconName}
        size="small"
        appearance="neutral"
        color={designSystemToken('semantic.fg.neutral')}
      />
      {label ? (
        <Label size="md" color={designSystemToken('semantic.fg.primary')}>
          {label}
        </Label>
      ) : null}
    </WrapperElement>
  );
}

// Preset preview
function createMockCaptions(presetName: string): Caption[] {
  const duration = 3;
  return [
    {
      id: 'preview-caption',
      text: presetName,
      start: 0,
      end: duration,
    },
  ];
}

function useLoopingTime(duration_in_ms = 3000) {
  const startTimeRef = useRef<number>(Date.now());

  const getCurrentTime = useCallback(() => {
    const now = Date.now();
    const elapsed = now - startTimeRef.current;

    // Reset the start time when we complete a cycle to prevent overflow
    if (elapsed >= duration_in_ms) {
      startTimeRef.current = now - (elapsed % duration_in_ms);
    }

    const currentElapsed = now - startTimeRef.current;
    return currentElapsed / 1000;
  }, [duration_in_ms]);

  return getCurrentTime;
}

interface PreviewCardProps {
  style: CaptionsStylePreset;
  showAnimation?: boolean;
}

function PreviewCard({style, showAnimation = false}: PreviewCardProps) {
  const getCurrentTime = useLoopingTime(3000);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({width: 280, height: 164});

  const mockCaptions = !showAnimation
    ? createMockCaptions(style.name)
    : createMockCaptions(`${style.name} Style`);

  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setCanvasSize({
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      });
    }
  }, []);

  const editedStyle = contentLabActions.getEditedStyle(style.id);
  const styleToUse = editedStyle || style;

  const centeredStyle = {
    ...styleToUse.captionsStyle,
    normalizedVerticalPosition: 0.5,
    animation: showAnimation ? styleToUse.captionsStyle.animation : null,
    typography: {
      ...styleToUse.captionsStyle.typography,
      fontSettings: {
        ...styleToUse.captionsStyle.typography.fontSettings,
        size: 74,
      },
    },
  };

  return (
    <PreviewContainer ref={containerRef}>
      <VideoCaptionOverlayStandalone
        testID={`preview-${style.id}`}
        captionsData={{captions: mockCaptions, languageCode: 'en'}}
        style={centeredStyle}
        getCurrentTime={getCurrentTime}
        animationRequired={showAnimation}
        canvasWidth={canvasSize.width}
        canvasHeight={canvasSize.height}
      />
    </PreviewContainer>
  );
}

interface StyleSelectorProps {
  styles: CaptionsStylePreset[];
  onStyleSelect: (style: CaptionsStylePreset) => void;
  onCustomize: (style: CaptionsStylePreset) => void;
  onReset?: (style: CaptionsStylePreset) => void;
  selectedStyle: CaptionsStylePreset | null;
  editedStyles?: Set<string>;
}

function StyleSelector({
  styles,
  onStyleSelect,
  onCustomize,
  onReset,
  selectedStyle,
  editedStyles = new Set(),
}: StyleSelectorProps) {
  const [hoveredStyleId, setHoveredStyleId] = useState<string | null>(null);

  const handleCardClick = (style: CaptionsStylePreset) => {
    onStyleSelect(style);
  };

  const handleEditClick = (e: React.MouseEvent, style: CaptionsStylePreset) => {
    e.stopPropagation();
    onStyleSelect(style);
    onCustomize(style);
  };

  const handleResetClick = (
    e: React.MouseEvent,
    style: CaptionsStylePreset
  ) => {
    e.stopPropagation();
    if (onReset) {
      onReset(style);
    }
  };

  const styleCardContainer = (
    isSelected: boolean,
    isEdited: boolean,
    style: CaptionsStylePreset
  ) => {
    const isHovered = hoveredStyleId === style.id;

    return (
      <StyleCard
        key={style.id}
        isSelected={isSelected}
        onClick={() => handleCardClick(style)}
        onDoubleClick={(e) => handleEditClick(e, style)}
        onMouseEnter={() => setHoveredStyleId(style.id)}
        onMouseLeave={() => setHoveredStyleId(null)}
      >
        <PreviewCard style={style} showAnimation={isHovered} />

        <HoverButtons className="hover-buttons" isActive={isSelected}>
          {isEdited && onReset ? (
            <HoverButton
              iconName="Actions-Reset"
              onClick={(e) => handleResetClick(e, style)}
              ariaLabel="Reset style to original"
            />
          ) : (
            <div />
          )}
          <HoverButton
            iconName="Features-Makeup-Liner"
            onClick={(e) => handleEditClick(e, style)}
            ariaLabel="Edit style"
            label={translate('Edit')}
          />
        </HoverButtons>
      </StyleCard>
    );
  };

  return (
    <Container>
      <ContentWrapper>
        <StylesGrid>
          {styles.map((style) => {
            const isSelected = selectedStyle?.id === style.id;
            const isEdited = editedStyles.has(style.id);

            return styleCardContainer(isSelected, isEdited, style);
          })}
        </StylesGrid>
      </ContentWrapper>
    </Container>
  );
}

export default StyleSelector;
